import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import type { ApiTheme } from "@/features/themes/types";
import type { ApiDilemma } from "@/features/dilemmas/types";
import { catchError } from "@/lib/errors";
import { queryClient } from "@/lib/query-client";
import { dilemmasRepo } from "@/lib/repositories";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import AiWritingAssistantInputView from "./ai-writing-assistant-input-view";
import AiWritingAssistantReviewView from "./ai-writing-assistant-review-view";
import AiWritingAssistantReviewSkeleton from "./ai-writing-assistant-review-skeleton";
import { useGenerateDilemma } from "../hooks/useGenerateDilemma";
import { Loader, Sparkle, Trash } from "lucide-react";

type AiWritingAssistantView = "input" | "generating" | "review";

interface AiWritingAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AiWritingAssistantDialog = ({
  open,
  onOpenChange,
}: AiWritingAssistantDialogProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const defaultLanguage: "fr" | "en" = i18n.language.startsWith("en")
    ? "en"
    : "fr";
  const [view, setView] = useState<AiWritingAssistantView>("input");
  const [selectedTheme, setSelectedTheme] = useState<ApiTheme | null>(null);
  const [idea, setIdea] = useState("");
  const [sourceLanguage, setSourceLanguage] = useState<"fr" | "en">(
    defaultLanguage,
  );
  const [dilemma, setDilemma] = useState<ApiDilemma | null>(null);
  const [refineNonce, setRefineNonce] = useState(0);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const { generate } = useGenerateDilemma();

  function resetState() {
    setView("input");
    setSelectedTheme(null);
    setIdea("");
    setSourceLanguage(defaultLanguage);
    setDilemma(null);
    setRefineNonce(0);
  }

  function closeAndReset() {
    setConfirmCloseOpen(false);
    resetState();
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    // A generated draft exists server-side but wasn't published: instead of
    // silently discarding it, ask whether to keep or delete it.
    if (dilemma) {
      setConfirmCloseOpen(true);
      return;
    }
    closeAndReset();
  }

  async function handleDeleteDraft() {
    if (!dilemma) {
      closeAndReset();
      return;
    }
    setIsDeletingDraft(true);
    const [error] = await catchError(dilemmasRepo.delete(dilemma.id));
    setIsDeletingDraft(false);
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
    closeAndReset();
  }

  async function handleGenerate() {
    setView("generating");
    const [error, newDilemma] = await catchError(
      generate({
        source_language: sourceLanguage,
        topic_id: selectedTheme?.id,
        description: idea.trim() || undefined,
      }),
    );
    if (error || !newDilemma) {
      toast.error(
        error
          ? handleApiError(error).message
          : t("modules.dilemmas.ai_assistant.generation_error"),
      );
      setView("input");
      return;
    }
    handleGenerated(newDilemma);
  }

  function handleGenerated(newDilemma: ApiDilemma) {
    setDilemma(newDilemma);
    setView("review");
    // The draft now exists server-side: refresh the listing so it shows up if
    // the user keeps it.
    void queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  }

  function handleRefined(newDilemma: ApiDilemma) {
    setDilemma(newDilemma);
    setRefineNonce((nonce) => nonce + 1);
    void queryClient.invalidateQueries({ queryKey: ["dilemmas"] });
  }

  function handlePublished() {
    // Published: reset without going through handleOpenChange so the draft
    // (now published) is not deleted.
    resetState();
    onOpenChange(false);
  }

  const titleKey =
    view === "input"
      ? "modules.dilemmas.ai_assistant.modal_title"
      : view === "generating"
        ? "modules.dilemmas.ai_assistant.generating_title"
        : "modules.dilemmas.ai_assistant.review_title";
  const title = t(titleKey);
  const isGenerating = view === "generating";

  const body = (
    <div
      key={view}
      className="animate-in fade-in-0 slide-in-from-bottom-2 duration-500 ease-out"
    >
      {view === "input" ? (
        <AiWritingAssistantInputView
          selectedTheme={selectedTheme}
          setSelectedTheme={setSelectedTheme}
          idea={idea}
          setIdea={setIdea}
          sourceLanguage={sourceLanguage}
          setSourceLanguage={setSourceLanguage}
          onCancel={() => handleOpenChange(false)}
          onGenerate={handleGenerate}
        />
      ) : view === "generating" ? (
        <AiWritingAssistantReviewSkeleton />
      ) : dilemma ? (
        <AiWritingAssistantReviewView
          key={`${dilemma.id}-${refineNonce}`}
          dilemma={dilemma}
          onRefined={handleRefined}
          onPublished={handlePublished}
          onCancel={() => handleOpenChange(false)}
        />
      ) : null}
    </div>
  );

  const confirmTitle = t("modules.dilemmas.ai_assistant.close_confirm_title");
  const confirmDescription = t(
    "modules.dilemmas.ai_assistant.close_confirm_description",
  );
  const deleteDraftButton = (
    <Button
      variant="destructive"
      onClick={handleDeleteDraft}
      disabled={isDeletingDraft}
    >
      {isDeletingDraft ? (
        <Loader className="animate-spin" />
      ) : (
        <Trash className="size-4" />
      )}
      {t("modules.dilemmas.ai_assistant.close_confirm_delete")}
    </Button>
  );
  const keepDraftButton = (
    <Button
      variant="outline"
      onClick={closeAndReset}
      disabled={isDeletingDraft}
    >
      {t("modules.dilemmas.ai_assistant.close_confirm_keep")}
    </Button>
  );

  const confirmClose = isMobile ? (
    <Drawer open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>{confirmTitle}</DrawerTitle>
          <DrawerDescription>{confirmDescription}</DrawerDescription>
        </DrawerHeader>
        <DrawerFooter className="pt-2">
          {deleteDraftButton}
          {keepDraftButton}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{confirmTitle}</DialogTitle>
          <DialogDescription>{confirmDescription}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {keepDraftButton}
          {deleteDraftButton}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (isMobile) {
    return (
      <>
        <Drawer open={open} onOpenChange={handleOpenChange}>
          <DrawerContent className="max-h-[92vh]">
            <DrawerHeader className="text-left">
              <DrawerTitle className={cn(isGenerating && "shimmer w-fit")}>
                {title}
              </DrawerTitle>
              <DrawerDescription className="sr-only">{title}</DrawerDescription>
            </DrawerHeader>
            <div className="overflow-y-auto px-4 pb-4">{body}</div>
          </DrawerContent>
        </Drawer>
        {confirmClose}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Sparkle className="text-primary" />
              <DialogTitle className={cn(isGenerating && "shimmer w-fit")}>
                {title}
              </DialogTitle>
            </div>
            <DialogDescription className="sr-only">{title}</DialogDescription>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
      {confirmClose}
    </>
  );
};

export default AiWritingAssistantDialog;
